import { useState, useEffect, useRef } from 'react';

/**
 * Universal hook for subscribing to Poof real-time data.
 *
 * @param subscribeFn - The subscribe function from the generated SDK: must start with subscribe or subscribeMany
 * @param enabled - Whether to enable the subscription
 * @param args - All arguments to pass to the subscribe function from the param subscribeFn like the path params or query params
 * @returns Object containing data, loading state, and error state
 *
 * @example
 * // For a single entity
 * const { data, loading, error } = useRealtimeData<PostsData | null>(
 *   subscribePost,
 *   true,
 *   postId
 * );
 *
 * // For a collection
 * const { data, loading, error } = useRealtimeData<PostsData[]>(
 *   subscribeManyPosts,
 *   true,
 *   "where title contains 'React'"
 * );
 *
 * // For nested resources
 * const { data, loading, error } = useRealtimeData<CommentsData | null>(
 *   subscribeComment,
 *   true,
 *   postId,
 *   commentId
 * );
 */
export function useRealtimeData<T>(
  subscribeFn: (callback: (data: T) => void, ...args: any[]) => Promise<() => void>,
  enabled: boolean,
  ...args: any[]
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  // Keep a stable ref to the active unsubscribe fn so the visibilitychange
  // handler can tear down the dead socket without triggering re-renders.
  const unsubscribeRef = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    if (!enabled) {
      // Do NOT reset data to null — preserve the last known value so consumers
      // (e.g. navbar counters) don't flicker to 0 during auth re-initialisation.
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    function startSubscription() {
      // Tear down any previous subscription before opening a new one.
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = undefined;
      }

      // Keep existing data visible while re-subscribing — don't flash empty.
      // Only set loading if we have no data yet (initial load).
      setData((prev) => {
        if (prev === null) setLoading(true);
        return prev;
      });
      setError(null);

      const callback = (newData: T) => {
        if (!cancelled) {
          setData(newData);
          setLoading(false);
        }
      };

      subscribeFn(callback, ...args)
        .then((unsub) => {
          if (cancelled) {
            unsub();
          } else {
            unsubscribeRef.current = unsub;
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setError(err instanceof Error ? err : new Error(String(err)));
            setLoading(false);
          }
        });
    }

    // Initial subscription.
    startSubscription();

    // Re-subscribe when the tab becomes visible again after being hidden.
    // This recovers from WebSocket timeouts that occurred while the tab was away.
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible' && !cancelled) {
        startSubscription();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = undefined;
      }
    };
  }, [subscribeFn, enabled, ...args]); // Dependencies are all the args

  return { data, loading, error };
}
